import React, { useEffect, useState } from 'react';
import { forwardRef, useImperativeHandle } from 'react';
import { Form } from '@ant-design/compatible';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import produce from 'immer';

import Table from '../../../../../components/_table/Table/Table';
import ExcelUploadForm from '../../../../../components/form/ExcelUploadForm';
import Anchor from '../../../../../components/link/Anchor';
import Button from '../../../../../components/Button';
import ProductListModal from './ProductListModal';
import { columns } from './columns';
import { applyFilterOption, applyPagination, convertToTableData } from './utils';
import useExcel from './hooks/useExcel';
import { INITIAL_OPTION_MODEL } from './constants';
import { PRODUCT_UPLOAD_TEMPLATE_XLSX_URL } from '../../../../../constants/service/common/staticFileUrlConstants';
import { TableOption } from './types';
import { ProductItem } from '../../types';
import { EventDetailProductItem } from '../../../../../types/query/event/detail';
import { SelectProductAndTagListStyle } from '../common/style';
import { asyncSimpleModal } from '../../../../../lib/common';

interface SelectProductListRef {
  value?: ProductItem[];
  onChange?: (value: ProductItem[]) => void;
}
interface SelectProductListProps {
  value?: ProductItem[];
  max?: number;
  onChange?: (value: ProductItem[]) => void;
}
const SelectProductList = forwardRef<SelectProductListRef, SelectProductListProps>((props, ref) => {
  const { value = [], max, onChange } = props;
  const [isProductListModal, setIsProductListModal] = useState(false);
  const [checkedIdList, setCheckedIdList] = useState<number[]>([]);
  const [tableOption, setTableOption] = useState<TableOption>(INITIAL_OPTION_MODEL);
  const { filterOption, pagination } = tableOption;
  const { isExcelUploadSuccess, downloadSelectedExcel, uploadExcel } = useExcel({ value, onChange });

  useImperativeHandle(ref, () => ({
    value,
    onChange,
  }));

  useEffect(() => {
    if (!isExcelUploadSuccess) return;

    resetOption();
  }, [isExcelUploadSuccess]);

  const resetOption = () => {
    setTableOption(INITIAL_OPTION_MODEL);
  };

  const openProductListModal = () => {
    setIsProductListModal(true);
  };

  const addProductList = async (newProductItem: ProductItem[]) => {
    if (!onChange) return;

    const changeValue = [...newProductItem, ...value];
    if (max && changeValue.length > max) {
      await asyncSimpleModal({
        type: 'error',
        title: '아이템 개수 초과',
        message: `기획전 아이템 개수는 최대 ${max} 입니다.`,
      });
      return;
    }

    onChange(changeValue);
    setIsProductListModal(false);
    resetOption();
  };

  const deleteProductList = () => {
    if (!onChange) return;

    const checkedIdListSet = new Set(checkedIdList);
    setCheckedIdList((prev) => prev.filter((item) => !checkedIdListSet.has(item)));
    const deletedValue = value.filter((item) => !checkedIdListSet.has(item.id));

    onChange(deletedValue);
    setTableOption((prevValue) => {
      return produce(prevValue, (draft) => {
        // 현재 페이지에 상품이 모두 삭제되었을 경우 이전 페이지를 가리켜야함
        const currentPage = draft.pagination.pageNum;
        const totalPage = Math.ceil(deletedValue.length / pagination.pageSize) - 1;
        if (totalPage < 0) {
          draft.pagination.pageNum = 0;
        } else if (currentPage > totalPage) {
          draft.pagination.pageNum = totalPage;
        } else {
          draft.pagination.pageNum = currentPage;
        }
      });
    });
  };

  const changeProductListOrder = (data: { id: number[]; list: ProductItem[] }) => {
    // 파라미터로 넘겨받는 리스트의 경우 현재 테이블에서 보여지는 리스트의 대한 정보만 넘겨주기 때문에
    // 전체 리스트에서 현재 페이지에 대한 리스트만 교체해서 변경해 주어야 전체 리스트가 유지됨
    if (!onChange) return;

    const { pageNum, pageSize } = pagination;
    const firstProductIndex = pageNum * pageSize;
    const currentListSize = data.list.length;
    const firstListSegment = value.slice(0, firstProductIndex);
    const lastListSegment = value.slice(firstProductIndex + currentListSize);

    onChange([...firstListSegment, ...data.list, ...lastListSegment]);
  };

  const listWithAppliedFilterOptions = applyFilterOption(value, filterOption);
  const visibleList = applyPagination(listWithAppliedFilterOptions, pagination);
  return (
    <>
      <SelectProductAndTagListStyle>
        <div className="item-counter">
          등록된 아이템 개수 : <span>{value.length}</span> ㅣ 선택된 아이템 개수 : <span>{checkedIdList.length}</span>
        </div>
        <div className="button-wrap">
          <div className="right">
            <Button isRadius color="black" text="상품 추가" onClick={openProductListModal} />
            <Button isRadius color="light" text="필터 초기화" onClick={resetOption} />
          </div>
          <div className="left">
            <Anchor
              text="업로드 템플릿"
              isUnderLine
              color="primary"
              href={PRODUCT_UPLOAD_TEMPLATE_XLSX_URL}
              target="_blank"
            />

            <ExcelUploadForm onChange={(file: File) => uploadExcel(file)} btnText="엑셀 업로드" />

            <Button isRadius color="light" text="엑셀 다운로드" onClick={() => downloadSelectedExcel()} />
          </div>
        </div>
        <Table
          rowKey={(record: ProductItem) => record.id}
          columns={(utils: any) => columns({ ...utils })}
          list={visibleList}
          size="middle"
          scroll={{ x: true }}
          bordered={true}
          onCheck={(list: number[]) => setCheckedIdList(list)}
          selectedList={checkedIdList}
          getList={(data: TableOption) => {
            setTableOption({
              ...tableOption,
              ...data,
            });
          }}
          pagination={{
            ...pagination,
            totalCnt: listWithAppliedFilterOptions.length,
          }}
          filterOption={filterOption}
          canDrag={true}
          onDragEnd={changeProductListOrder}
        />
        <div className="button-wrap">
          <Button isRadius color="primary" text="삭제" disabled={!checkedIdList.length} onClick={deleteProductList} />
        </div>
      </SelectProductAndTagListStyle>
      {isProductListModal && (
        <ProductListModal
          closeModal={() => setIsProductListModal(false)}
          selectedProductIdList={value.map((item) => item.id)}
          onSubmit={addProductList}
        />
      )}
    </>
  );
});
SelectProductList.displayName = 'SelectProductList';

interface SelectProductListFormProps extends FormComponentProps {
  name: string;
  initialValue?: EventDetailProductItem[];
  max?: number;
}
const SelectProductListForm = (props: SelectProductListFormProps) => {
  const { form, name, initialValue, max } = props;
  const { getFieldDecorator } = form;

  return (
    <div>
      <Form.Item>
        {getFieldDecorator(name, {
          initialValue: convertToTableData(initialValue),
        })(<SelectProductList max={max} />)}
      </Form.Item>
    </div>
  );
};

export default SelectProductListForm;
